import styled from "@emotion/styled"
import type { Locale } from "@porsche-customer/slfinrtl-context-types"
import { PButton, PInlineNotification, PText } from "@porsche-design-system/components-react"
import { spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { AddMarketExportBody, IsoDate, MarketExportType, MarketExportView } from "@slfinrtl/ui-bff-routes"
import { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"
import { formMessages } from "src/common/form.messages"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import type { RadioOption } from "src/components"
import { ButtonGroup, FormLayout, ProgressBar, RadioGroup } from "src/components"
import { getExportTimeFrameDates, validateExportTimeFrame } from "../common/exportTimeFrame"
import type { ExportTimeFrame, ExportTimeFrameFormValues } from "../common/exportTimeFrame"
import { marketExportMessages } from "../common/marketExport.messages"
import { MarketExportTimeFrameFields } from "./MarketExportTimeFrameFields"

export interface MarketExportModalContentProps {
	error: string | null
	initialExportType?: MarketExportType
	initialTimeFrame?: ExportTimeFrame
	isLoading: boolean
	onCancel: () => void
	onSubmit: (request: AddMarketExportBody, timeFrame?: ExportTimeFrame) => void
	progress?: number
	today: Date
	view: MarketExportView
}

export const MarketExportModalContent = ({
	error,
	initialExportType,
	initialTimeFrame = "currentMonth",
	isLoading,
	onCancel,
	onSubmit,
	progress,
	today,
	view,
}: MarketExportModalContentProps) => {
	const { formatDisplayName, formatMessage } = useIntl()
	const [selectedLocale, setSelectedLocale] = useState<Locale>(view.locales[0])
	const [exportType, setExportType] = useState<MarketExportType>(
		initialExportType ?? (view.isInventoryExportAvailable ? "INVENTORY" : "DELIVERED_INVENTORY"),
	)
	const form = useForm({
		defaultValues: {
			...getExportTimeFrameDates(
				initialTimeFrame === "customSelection" ? "currentMonth" : initialTimeFrame,
				today,
			),
			timeFrame: initialTimeFrame,
		},
		mode: "onChange",
	})

	const onValidSubmit = ({ endDate, startDate, timeFrame }: ExportTimeFrameFormValues) => {
		if (exportType === "INVENTORY") {
			onSubmit({ exportType, locale: selectedLocale })
			return
		}

		const validationMessage = validateExportTimeFrame(startDate, endDate)
		if (validationMessage) {
			form.setError(
				"startDate",
				{ message: formatMessage(validationMessage), type: "validate" },
				{ shouldFocus: true },
			)
			return
		}
		onSubmit(
			{
				deliveryEndDate: endDate as IsoDate,
				deliveryStartDate: startDate as IsoDate,
				exportType,
				locale: selectedLocale,
			},
			timeFrame,
		)
	}

	const changeExportType = (value: MarketExportType) => {
		setExportType(value)
		form.reset()
	}

	const vehicleStatusOptions: RadioOption<MarketExportType>[] = [
		{ label: formatMessage(marketExportMessages.inStockVehicles), value: "INVENTORY" },
		{ label: formatMessage(marketExportMessages.deliveredVehicles), value: "DELIVERED_INVENTORY" },
	]

	const languageOptions = view.locales.map((value) => {
		const languageCode = value.split("-")[0]
		const displayName = formatDisplayName(languageCode, { type: "language" })
		return {
			label: displayName ?? languageCode,
			value,
		}
	})

	return (
		<FormProvider {...form}>
			<FormLayout>
				{languageOptions.length > 1 && (
					<RadioGroup
						name="language"
						label={formatMessage(marketExportMessages.language)}
						value={selectedLocale}
						onChange={setSelectedLocale}
						required
						options={languageOptions}
					/>
				)}

				{view.isInventoryExportAvailable && view.isDeliveredExportAvailable ? (
					<RadioGroup
						name="exportType"
						label={formatMessage(marketExportMessages.vehicleStatus)}
						value={exportType}
						onChange={changeExportType}
						required
						options={vehicleStatusOptions}
					/>
				) : (
					<PText>
						<strong>{formatMessage(marketExportMessages.vehicleStatus)}:</strong>{" "}
						{vehicleStatusOptions.find((option) => option.value === exportType)?.label}
					</PText>
				)}
				{exportType === "DELIVERED_INVENTORY" && <MarketExportTimeFrameFields today={today} />}

				<PText>
					<FormattedMessage {...marketExportMessages.intro} />
					<br />
					<strong>{view.disclaimer}</strong>
				</PText>

				<ButtonGroup withoutMarginTop>
					<PButton
						icon="download"
						loading={isLoading}
						onClick={() => void form.handleSubmit(onValidSubmit)()}
					>
						<FormattedMessage {...marketExportMessages.downloadAsExcel} />
					</PButton>
					<PButton type="button" icon="close" variant="secondary" onClick={onCancel}>
						<FormattedMessage {...formMessages.cancel} />
					</PButton>
				</ButtonGroup>

				<ProgressWrapper>
					{error ? (
						<PInlineNotification state="error" dismissButton={false}>
							{error}
						</PInlineNotification>
					) : (
						// always mounted to reserve its height, hidden instead of unmounted to avoid the modal jumping
						<StyledProgressBar $visible={isLoading} value={progress ?? 0} />
					)}
				</ProgressWrapper>
			</FormLayout>
		</FormProvider>
	)
}

const ProgressWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
`

const StyledProgressBar = styled(ProgressBar, { shouldForwardProp })<{ $visible: boolean }>`
	visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
`
